import { useRef, useState } from 'react';

interface ProductImageUploadProps {
  imageUrl?: string;
  fallbackImageUrl?: string;
  onImageChange: (file: File | null) => void;
  disabled?: boolean;
  containerClassName?: string;
}

export function ProductImageUpload({
  imageUrl,
  fallbackImageUrl,
  onImageChange,
  disabled = false,
  containerClassName
}: ProductImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const resolvedImageUrl = imageUrl || fallbackImageUrl;
  const isFallbackImage = !imageUrl && !!fallbackImageUrl;

  const handleImageClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 서버 제한: 20MB 초과 방지
      if (file.size > 20 * 1024 * 1024) {
        alert('이미지는 20MB 이하만 업로드 가능합니다.');
        return;
      }
      onImageChange(file);
    }
  };

  const handleImageRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onImageChange(null);
  };

  const validateAndProcessFile = (file: File) => {
    // 이미지 파일인지 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }
    // 서버 제한: 20MB 초과 방지
    if (file.size > 20 * 1024 * 1024) {
      alert('이미지는 20MB 이하만 업로드 가능합니다.');
      return;
    }
    onImageChange(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    setIsDragOver(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      validateAndProcessFile(file);
    }
  };

  return (
    <div 
      className={`relative w-24 h-24 border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition-colors ${containerClassName || ''} ${
        disabled 
          ? 'border-gray-200 cursor-not-allowed' 
          : isDragOver 
            ? 'border-green-400 bg-green-50' 
            : 'border-gray-300 hover:border-gray-400'
      }`}
      onClick={handleImageClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
        disabled={disabled}
      />
      
      {resolvedImageUrl ? (
        <>
          <img 
            src={resolvedImageUrl} 
            alt="상품 이미지" 
            className="w-full h-full object-cover"
          />
          {!disabled && !isFallbackImage && (
            <button
              onClick={handleImageRemove}
              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              ×
            </button>
          )}
        </>
      ) : (
        <div className={`w-full h-full flex flex-col items-center justify-center transition-colors ${
          isDragOver ? 'text-green-500' : 'text-gray-400'
        }`}>
          <svg className="w-8 h-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-xs">{isDragOver ? '드롭하세요' : '사진 추가'}</span>
        </div>
      )}
    </div>
  );
}
